import React, {createContext, useContext, useState} from 'react';
import { producaov2 } from '../../../../../services/apis';
import { promiseHandler } from '../../../../../utils/promises';

const defaultCertificateOptions = {
  userId: null, 
  courseId: null, 
  needRegenerate: true, 
  manualConclusionDate: '',
  manualStarterDate: '', 
  hideConclusionDate: false,
  isEnglish: false,
  internalProjectIds: [],
  externalProjectsName: [],
  includeEmenta: true,
  onlyEmenta: false,
  manualSignaturesId: [],
  eventCourseDays: '',
  workloadHours: '',
  manualEmenta: '',
  includeCourseSignatures: false,
  isCustomCertificate: false,
  needSendEmail: false,
  needSendEmailToManager: false,
  companyLogoFile: null,
  companyColor: ''
}

const initialCertificateOptions = {
  userId: null, 
  courseId: null,
  needRegenerate: true,
  includeEmenta: true,
  internalProjectIds: [],
  externalProjectsName: [],
  needSendEmail: false,
  needSendEmailToManager: false,
}

const defaultValues = {
  options: defaultCertificateOptions,
  submit: async () => {},
  updateOptions: ({optionName, optionValue}) => {},
  optionsError: {},
  submitError: '',
  reset: () => {},
  certificatesCount: 0
}

const normalizeOptions = (options = defaultCertificateOptions) => {
  const normalizedExternalProjectsName = options?.externalProjectsName?.filter(Boolean)?.map(project => project?.trim());
  const normalizedInternalProjectsId = options?.internalProjectIds?.filter(Boolean);

  return {
    ...options,
    externalProjectsName: normalizedExternalProjectsName,
    internalProjectIds: normalizedInternalProjectsId
  }
}

const CertificateContext = createContext(defaultValues);

export function CertificateProvider({children}) {
  const [options, setOptions] = useState(defaultCertificateOptions);
  const [optionsError, setOptionsError] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [certificatesCount, setCertificatesCount] = useState(0);

  const validateOptions = () => {
    let error = '';

    if(!options?.userId && !options?.onlyEmenta) {
      error = error + 'Informe um aluno; ';
    }

    const isSpecialist = (options?.internalProjectIds?.length + options?.externalProjectsName?.length) > 0;

    if(!options?.courseId && !isSpecialist) {
      error = error + 'Informe um curso; ';
    }

    setSubmitError(error);

    return !error;
  }
  
  const submit = async () => {
    const isValidOptions = validateOptions();

    if(!isValidOptions) {
      return null;
    }

    const normalizedOptions = normalizeOptions(options);

    const formData = new FormData();

    const { companyLogoFile, ...restNormalizedOptions } = normalizedOptions;

    formData.append('companyLogoFile', companyLogoFile);
    formData.append('config', JSON.stringify(restNormalizedOptions));

    const {data: response, error} = await promiseHandler(producaov2.post('admin/certificate', formData));

    if(error) {
      console.log(error)
      setSubmitError(JSON.stringify(error?.response?.data));
      return null;
    }

    return response?.data;
  }

  const reset = () => {
    setOptions(defaultCertificateOptions);
    setOptionsError({});
    setSubmitError('');
    setCertificatesCount(prev => prev + 1);
  }

  const updateOptions = ({optionName, optionValue}) => {
    if(!optionName) {
      throw new Error('É necessário informar uma opção valida');
    }

    if(!!optionsError[optionName]) {
      setOptionsError(prev => ({...prev, [optionName]: ''}));
    }

    setOptions(prev => ({...prev, [optionName]: optionValue}));

    if(!!submitError) {
      setSubmitError('');
    }
  }

  const values = {
    submit,
    updateOptions,
    options,
    optionsError,
    submitError,
    reset,
    certificatesCount
  }

  return (
    <CertificateContext.Provider value={values}>
      {children}
    </CertificateContext.Provider>
  )
}

export const useCertificate = () => useContext(CertificateContext);